import { Avatar, Box, Typography } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import NoPhotographyIcon from "@mui/icons-material/NoPhotography";
import { Colors } from "Components/Layout/Themes/Colors";
import { BlTextButton } from "Components/Shared/Buttons/BlTextButton";
import { CloseIcon } from "Components/Shared/Icons";
import { EditIcon } from "Components/Shared/Icons/Edit";
import { useRef } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { ImageOrNode } from "Components/Shared/ImageOrNode";

const RESOURCES = Resources.Components.Merchant.Logo;
const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";
const ACCEPTED_TYPE_LIST = ACCEPTED_TYPES.split(",");
const MAX_SIZE_BYTES = 2 * 1024 * 1024;

type Props = {
  imageUrl: string | undefined;
  isPending: boolean;
  onUpload: (file: File) => void;
  onDelete: () => void;
  placeholder?: React.ReactNode;
  hint?: string;
  isReadOnly?: boolean;
};

export const ImageUpload = ({
  imageUrl,
  isPending,
  onUpload,
  onDelete,
  placeholder,
  hint,
  isReadOnly = false,
}: Props) => {
  const { t } = useResource();
  const inputRef = useRef<HTMLInputElement>(null);
  const hasImage = !!imageUrl;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // The picker's `accept` is advisory ("All files" bypasses it) — check the type here so the
    // user gets a specific message instead of the generic upload failure from the server.
    if (!ACCEPTED_TYPE_LIST.includes(file.type)) {
      toast.error(t(RESOURCES.InvalidFileType));
      event.target.value = "";
      return;
    }

    if (file.size > MAX_SIZE_BYTES) {
      toast.error(t(RESOURCES.FileTooLarge));
      event.target.value = "";
      return;
    }

    onUpload(file);
    event.target.value = "";
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        borderRadius={2}
        py={2}
        px={3}
        sx={({ colors }) => ({ border: `1px solid ${colors.border}` })}
      >
        <Avatar
          src={imageUrl}
          variant="rounded"
          sx={{ width: 64, height: 64, bgcolor: "transparent" }}
        >
          {placeholder ? (
            typeof placeholder === "string" ? (
              <ImageOrNode imageUrl={placeholder} />
            ) : (
              <ImageOrNode image={placeholder} />
            )
          ) : (
            <NoPhotographyIcon sx={{ fontSize: 32, color: "text.disabled" }} />
          )}
        </Avatar>

        {!isReadOnly && (
          <Box display="flex" gap={3} alignItems="center">
            <BlTextButton
              onClick={() => inputRef.current?.click()}
              disabled={isPending}
            >
              {hasImage ? (
                <EditIcon width="20px" height="20px" />
              ) : (
                <AddCircleOutlineIcon />
              )}
            </BlTextButton>
            {hasImage && (
              <BlTextButton onClick={onDelete} disabled={isPending}>
                <CloseIcon
                  width="20px"
                  height="20px"
                  color={isPending ? Colors.DarkGrey : Colors.DeleteRed}
                />
              </BlTextButton>
            )}
          </Box>
        )}
      </Box>

      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        hidden
        onChange={handleFileChange}
      />
    </>
  );
};
